import {
	facebookImageMOValidator,
	facebookImageMOWarning,
	facebookImageValidator,
	facebookImageWarning,
	facebookVideoMOValidator,
	facebookVideoMOWarning,
	facebookVideoValidator,
	facebookVideoWarning,
	facebookCarouselValidator,
	facebookCarouselWarning,
	facebookCarouselMOValidator,
	facebookCarouselMOWarning
} from "social/validators/facebook";
import {
	googleDemandGenImageWarning,
	googleDemandGenImageValidator,
	googleDemandGenCarouselWarning,
	googleDemandGenCarouselValidator
} from "social/validators/google";
import {
	linkedInImageWarning,
	linkedInImageValidator,
	linkedInVideoWarning,
	linkedInVideoValidator,
	linkedInCarouselWarning,
	linkedInCarouselValidator
} from "social/validators/linkedIn";
import {
	pinterestImageWarning,
	pinterestImageValidator,
	pinterestVideoWarning,
	pinterestVideoValidator,
	pinterestMaxWidthVideoWarning,
	pinterestMaxWidthVideoValidator,
	pinterestCarouselWarning,
	pinterestCarouselValidator
} from "social/validators/pinterest";
import {
	snapchatImageWarning,
	snapchatImageValidator,
	snapchatVideoWarning,
	snapchatVideoValidator
} from "social/validators/snapchat";
import { tiktokVideoWarning, tiktokVideoValidator } from "social/validators/tiktok";
import {
	twitterImageWarning,
	twitterImageValidator,
	twitterVideoWarning,
	twitterVideoValidator,
	twitterCarouselWarning,
	twitterCarouselValidator
} from "social/validators/twitter";
import {
	youTubeDiscoveryWarning,
	youTubeDiscoveryValidator,
	youTubeInStreamWarning,
	youTubeInStreamValidator
} from "social/validators/youtube";

import { CREATIVE_ASSET_TYPES, SOCIAL_AD_TYPES } from "utils/constants";

import { requiredNotNull } from "widgets/forms/validators/validators";

export function requiredAsset(details) {
	if (!details?.id) {
		return "Field is required";
	}
}

export function requiredAPIOption(value){
	/*
		The API expects selected options to have `id` property, so values
		should be normalized to include them.

		See the `normalizeAPIOption` function in "social/utils"
	*/
	if(!(value?.id)){
		return "Field is required";
	}
}

export function conditionalRequiredNotNull(key) {
	return (value, values) => {
		if (values[key]) {
			return requiredNotNull(value);
		}
	};
}

export function conditionalValidator(key, validator, condition = item => item){
	/*
		This is a more generic version of the conditionalRequireNotNull validator.

		It will check the value of another field, run it through the `condition`,
		and if that passes, it will return the value of the `validator` function.
		By default, the condition just checks that the value is truthy.

		e.g. If you want to check if a value is not null only if another field has
		a dynamic value, you can use this like

		```
		<Field
			validate = {[conditionalValidator("otherField", requiredNotNull, item => item === item?.value)]}
		/>
		```
	*/
	return (value, allValues, props, name) => {
		if(condition(allValues[key])){
			return validator(value, allValues, props, name);
		}
	};
}

export function validateUnlessAdStudio(validators){
	/*
		This takes an array of validators, and just conditions each of them on whether
		the `assetType` is Ad Studio.

		If it is Ad Studio, the validator will just return undefined, preventing selected
		assets from blocking a save when they're not actually relevant.

		(We keep the asset selection in case the user switches back to pre-built assets.)
	*/
	return validators.map(validator => conditionalValidator(
		"assetType",
		validator,
		assetType => assetType !== CREATIVE_ASSET_TYPES.ADSTUDIO
	));
}

export function getCardWarnings(cards = [], allValues, cardValidators){
	const cardWarnings = [];
	cards.forEach((card) => {
		Object.entries(cardValidators).forEach(([field, validators]) => {
			const value = card[field];
			const { order } = card;

			validators.some((validator) => {
				let warning = validator(value, allValues, card, field);
				if(warning){
					if(!cardWarnings[order]){
						cardWarnings[order] = {};
					}
					cardWarnings[order][field] = warning;
				}
				return warning;
			});
		});
	});

	return cardWarnings;
}

export function cardValidator(cardValidatorConfig){
	return (values, allValues, props, name) => {
		const warnings = getCardWarnings(values, allValues, cardValidatorConfig, name);

		if(warnings.length){
			return warnings;
		}
	};
}

const MIN_CARDS = 2;
export function minimumCards(value, allValues){
	if(value?.length < MIN_CARDS){
		return `Carousels must contain at least ${MIN_CARDS} cards`;
	}
}
export function minCards(values, allValues, props, name){
	const { cards = []} = allValues;
	if(cards.length < MIN_CARDS){
		return `Carousels must contain at least ${MIN_CARDS} cards`;
	}
}

export function dynamicValidator(validator){
	/*
		This takes a validator function and returns a new function that applies
		it to validate the value of a dynamic field.

		This allows us to use existing validators, instead of requiring extra versions
		and duplication for every validator used on a dynamic field.
	*/
	return (value, ...rest) => validator(value?.hasOwnProperty?.("value") ? value?.value : value, ...rest);
}

export function makeDynamicConfig(validationConfig){
	/*
		This takes a configuration object, keyed by field name, with an array of validators
		for each key, and returns a new object with the validators adjusted for dynamic fields.
	*/
	return Object.entries(validationConfig).reduce((prev, [fieldName, validators]) => {
		prev[fieldName] = validators.map(dynamicValidator);
		return prev;
	}, {});
}

export function errorMessageReducer(validators, values, formName, props={}) {
	let hasErrors = false;
	const errors = Object.keys(validators).reduce((prev, key) => {
		let message = "";

		for (const validator of validators[key]) {
			message = validator(values[key], values, props, key);
			if (message) {
				hasErrors = true;
				return {
					...prev,
					[key]: message,
				};
			}
		}

		return prev;
	}, {});

	if (hasErrors) {
		return {
			[formName]: errors,
		};
	}
}

// Loops through the given warning/ validator map and returns the messages to redux-form
function allErrorsReducer(values, props, validatorMap, init={}) {
	return values.adTypes.reduce((prev, adType) => {
		const formatWarning = validatorMap[adType];

		if (!formatWarning) { return prev; }

		return {
			...prev,
			...formatWarning(values, props),
		};
	}, init);
}

export const WARNINGS_BY_ADTYPE = {
	[SOCIAL_AD_TYPES.FACEBOOK_IMAGE]: facebookImageWarning,
	[SOCIAL_AD_TYPES.FACEBOOK_VIDEO]: facebookVideoWarning,
	[SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL]: facebookCarouselWarning,
	[SOCIAL_AD_TYPES.FACEBOOK_IMAGE_MO]: facebookImageMOWarning,
	[SOCIAL_AD_TYPES.FACEBOOK_VIDEO_MO]: facebookVideoMOWarning,
	[SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL_MO]: facebookCarouselMOWarning,
	[SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_IMAGE]: googleDemandGenImageWarning,
	[SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_CAROUSEL]: googleDemandGenCarouselWarning,
	[SOCIAL_AD_TYPES.LINKEDIN_IMAGE]: linkedInImageWarning,
	[SOCIAL_AD_TYPES.LINKEDIN_VIDEO]: linkedInVideoWarning,
	[SOCIAL_AD_TYPES.LINKEDIN_CAROUSEL]: linkedInCarouselWarning,
	[SOCIAL_AD_TYPES.SNAPCHAT_IMAGE]: snapchatImageWarning,
	[SOCIAL_AD_TYPES.SNAPCHAT_VIDEO]: snapchatVideoWarning,
	[SOCIAL_AD_TYPES.YOUTUBE_DISCOVERY]: youTubeDiscoveryWarning,
	[SOCIAL_AD_TYPES.YOUTUBE_INSTREAM]: youTubeInStreamWarning,
	[SOCIAL_AD_TYPES.PINTEREST_IMAGE]: pinterestImageWarning,
	[SOCIAL_AD_TYPES.PINTEREST_VIDEO]: pinterestVideoWarning,
	[SOCIAL_AD_TYPES.PINTEREST_MAX_WIDTH_VIDEO]: pinterestMaxWidthVideoWarning,
	[SOCIAL_AD_TYPES.PINTEREST_CAROUSEL]: pinterestCarouselWarning,
	[SOCIAL_AD_TYPES.TWITTER_IMAGE]: twitterImageWarning,
	[SOCIAL_AD_TYPES.TWITTER_VIDEO]: twitterVideoWarning,
	[SOCIAL_AD_TYPES.TWITTER_CAROUSEL]: twitterCarouselWarning,
	[SOCIAL_AD_TYPES.TIKTOK_VIDEO]: tiktokVideoWarning,
};

export function warnAll(values, props){
	if (!values.adTypes) { return; }

	return allErrorsReducer(values, props, WARNINGS_BY_ADTYPE);
}

export const VALIDATORS_BY_ADTYPE = {
	[SOCIAL_AD_TYPES.FACEBOOK_IMAGE]: facebookImageValidator,
	[SOCIAL_AD_TYPES.FACEBOOK_VIDEO]: facebookVideoValidator,
	[SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL]: facebookCarouselValidator,
	[SOCIAL_AD_TYPES.FACEBOOK_IMAGE_MO]: facebookImageMOValidator,
	[SOCIAL_AD_TYPES.FACEBOOK_VIDEO_MO]: facebookVideoMOValidator,
	[SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL_MO]: facebookCarouselMOValidator,
	[SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_IMAGE]: googleDemandGenImageValidator,
	[SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_CAROUSEL]: googleDemandGenCarouselValidator,
	[SOCIAL_AD_TYPES.LINKEDIN_IMAGE]: linkedInImageValidator,
	[SOCIAL_AD_TYPES.LINKEDIN_VIDEO]: linkedInVideoValidator,
	[SOCIAL_AD_TYPES.LINKEDIN_CAROUSEL]: linkedInCarouselValidator,
	[SOCIAL_AD_TYPES.SNAPCHAT_IMAGE]: snapchatImageValidator,
	[SOCIAL_AD_TYPES.SNAPCHAT_VIDEO]: snapchatVideoValidator,
	[SOCIAL_AD_TYPES.YOUTUBE_DISCOVERY]: youTubeDiscoveryValidator,
	[SOCIAL_AD_TYPES.YOUTUBE_INSTREAM]: youTubeInStreamValidator,
	[SOCIAL_AD_TYPES.PINTEREST_IMAGE]: pinterestImageValidator,
	[SOCIAL_AD_TYPES.PINTEREST_VIDEO]: pinterestVideoValidator,
	[SOCIAL_AD_TYPES.PINTEREST_MAX_WIDTH_VIDEO]: pinterestMaxWidthVideoValidator,
	[SOCIAL_AD_TYPES.PINTEREST_CAROUSEL]: pinterestCarouselValidator,
	[SOCIAL_AD_TYPES.TWITTER_IMAGE]: twitterImageValidator,
	[SOCIAL_AD_TYPES.TWITTER_VIDEO]: twitterVideoValidator,
	[SOCIAL_AD_TYPES.TWITTER_CAROUSEL]: twitterCarouselValidator,
	[SOCIAL_AD_TYPES.TIKTOK_VIDEO]: tiktokVideoValidator,
};

export function validateAll(values, props) {
	if (!values.adTypes) { return; }

	return allErrorsReducer(values, props, VALIDATORS_BY_ADTYPE);
}
